import React, { useId } from 'react';

/**
 * Tiny inline SVG sparkline (no chart lib). Stroke uses `currentColor` so
 * it inherits the parent's text color — keeps dark-mode "just works".
 */
export default function StatSparkline({
  values = [],
  width = 72,
  height = 24,
  strokeWidth = 1.5,
  fill = true,
  ariaLabel,
}) {
  const gradId = useId();
  const points = Array.isArray(values) ? values.filter((v) => typeof v === 'number' && !isNaN(v)) : [];
  if (points.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        aria-hidden="true"
        style={{ display: 'block', opacity: 0.35, color: 'currentColor' }}
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const pad = strokeWidth;
  const yFor = (v) => pad + (1 - (v - min) / range) * (height - pad * 2);
  const coords = points.map((v, i) => [i * stepX, yFor(v)]);
  const lineD = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaD = `${lineD} L${width},${height} L0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'block', color: 'currentColor' }}
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaD} fill={`url(#${gradId})`} />
        </>
      )}
      <path
        d={lineD}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
